import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import { ApiError } from '../../api/problem'
import { describeApiFailure } from '../../lib/failure'
import { campaignKeys } from '../campaigns/queries'
import { characterKeys } from '../characters/queries'
import { galleryKeys } from '../images/queries'
import type { Gallery, GalleryOwner } from '../images/types'
import {
  composerOwnerKey,
  composerPath,
  type ComposerDraft,
  type ComposerOwner,
  type LocationImageType,
} from './types'

export const composerKeys = {
  all: ['composer'] as const,
  drafts: (owner: ComposerOwner) =>
    [...composerKeys.all, 'drafts', ...composerOwnerKey(owner)] as const,
  snapshot: (owner: ComposerOwner) =>
    [...composerKeys.all, 'snapshot', ...composerOwnerKey(owner)] as const,
}

/** The gallery this composer's results are saved into. */
function galleryOwner(owner: ComposerOwner): GalleryOwner {
  return owner.kind === 'character'
    ? { kind: 'character', characterId: owner.characterId }
    : { kind: 'location', campaignId: owner.campaignId, locationId: owner.locationId }
}

/**
 * The caller's unsaved drafts for this subject.
 *
 * Server-held rather than kept in component state, which is what makes a reload — or a phone
 * that backgrounded the tab mid-generation — cost nothing. A composer refetches this when a
 * generation settles, because that is where the new image is.
 */
export function useDrafts(owner: ComposerOwner, enabled = true): UseQueryResult<ComposerDraft[]> {
  return useQuery({
    queryKey: composerKeys.drafts(owner),
    enabled,
    queryFn: () => api.get<ComposerDraft[]>(`${composerPath(owner)}/drafts`),
  })
}

/**
 * The subject's own words as prompt material — a character's profile, or a location's
 * description.
 *
 * Fetched only when the tool that uses it is opened: it is a few thousand characters nobody
 * asked for until they did, and a composer opens far more often than the tool does.
 */
export function useSnapshot(
  owner: ComposerOwner,
  enabled: boolean,
): UseQueryResult<{ text: string }> {
  const path =
    owner.kind === 'character'
      ? `${composerPath(owner)}/profile-snapshot`
      : `${composerPath(owner)}/description-snapshot`

  return useQuery({
    queryKey: composerKeys.snapshot(owner),
    enabled,
    // The source changes rarely and the snapshot is expensive to assemble; a stale one for the
    // length of a composing session is not a problem — the user edits it before sending anyway.
    staleTime: 5 * 60 * 1000,
    queryFn: () => api.get<{ text: string }>(path),
  })
}

/**
 * What can be done with a draft: keep it, or throw it away.
 *
 * **Saving is the only thing here that writes.** That asymmetry is a composer's whole shape —
 * generating costs model time and produces nothing durable, and a picture becomes one the
 * subject *has* only when someone says so.
 *
 * A save answers with the whole post-save gallery, so the gallery cache is written directly
 * rather than invalidated — the same collapse of the envelope rule the gallery's own mutations
 * make. What is *invalidated* differs by owner and is not cosmetic: a first saved portrait
 * becomes a character's main image and the sheet header renders it, and a saved location image
 * changes the thumbnail the city page draws.
 *
 * **A battle map save can conflict**, and that is handled rather than surfaced as a failure. A
 * location holds at most one battle map, so the API answers `409` for the second save unless the
 * caller says to replace. `save` reports that as `conflict`, which the results card turns into a
 * question; `saveReplacing` is the answer.
 */
export function useDraftActions(owner: ComposerOwner) {
  const queryClient = useQueryClient()
  const path = composerPath(owner)
  const gallery = galleryOwner(owner)

  const [failure, setFailure] = useState<string | null>(null)

  /** The draft whose save hit the one-battle-map rule, if any. */
  const [conflict, setConflict] = useState<string | null>(null)

  const forget = (draftId: string) => {
    queryClient.setQueryData<ComposerDraft[]>(composerKeys.drafts(owner), (drafts) =>
      (drafts ?? []).filter((draft) => draft.draftId !== draftId),
    )
  }

  const onSaved = (result: Gallery, input: { draftId: string }) => {
    setFailure(null)
    setConflict(null)
    queryClient.setQueryData<Gallery>(galleryKeys.gallery(gallery), result)

    if (owner.kind === 'character') {
      void queryClient.invalidateQueries({ queryKey: characterKeys.sheet(owner.characterId, null) })
    } else {
      // The world sheet carries each location's imageUrl and battleMapImageUrl, and the city
      // page's thumbnail is the visible proof that a save landed.
      void queryClient.invalidateQueries({ queryKey: campaignKeys.world(owner.campaignId) })
    }

    forget(input.draftId)
  }

  const onError = (error: unknown) => {
    setFailure(describeApiFailure(error, 'That draft could not be reached.'))
  }

  const save = useMutation({
    mutationFn: (input: { draftId: string; title?: string; replaceExisting?: boolean }) =>
      api.post<Gallery>(`${path}/drafts/${input.draftId}/save`, {
        title: input.title === undefined || input.title === '' ? null : input.title,
        replaceExisting: input.replaceExisting ?? false,
      }),
    onSuccess: onSaved,
    onError: (error: unknown, input) => {
      // 409 is not a failure to report — it is a question to ask. The location already has a
      // battle map, and replacing it is a decision the person saving has to make on purpose.
      if (error instanceof ApiError && error.status === 409) {
        setFailure(null)
        setConflict(input.draftId)
        return
      }

      onError(error)
    },
  })

  const discard = useMutation({
    mutationFn: (input: { draftId: string }) => api.delete<void>(`${path}/drafts/${input.draftId}`),
    onSuccess: (_result, input) => {
      setFailure(null)
      if (conflict === input.draftId) setConflict(null)
      forget(input.draftId)
    },
    onError,
  })

  return {
    save,
    discard,
    failure,
    conflict,
    dismissConflict: useCallback(() => setConflict(null), []),
    dismissFailure: useCallback(() => setFailure(null), []),
  }
}

export type DraftActions = ReturnType<typeof useDraftActions>

/** What a location composer sends beyond the blocks. Character composers send neither. */
export interface LocationComposeExtras {
  imageType: LocationImageType
}
